import React, { useState, useMemo } from 'react';
import { detectInAppBrowser, openInExternalBrowser } from '../../utils/inAppBrowser.js';

export default function GoogleLoginButton() {
  const inAppInfo = useMemo(() => detectInAppBrowser(), []);
  const [copied, setCopied] = useState(false);

  const handleGoogleLogin = async () => {
    if (inAppInfo.isInApp) return;

    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID ||
                     '163927207983-orrlcm0uf3s386of8mk8cmt6q5ifedk8.apps.googleusercontent.com';

    const redirectUri = `${window.location.origin}/api/auth/google/callback`;
    const scope = 'email profile openid';
    const state = generateRandomState();

    try {
      await fetch('/api/session/update', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: 'oauth_state',
          value: state
        })
      });
    } catch (error) {
      console.error('[Google Login] Failed to save state to session:', error);
    }

    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', clientId);
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', scope);
    authUrl.searchParams.set('access_type', 'offline');
    authUrl.searchParams.set('prompt', 'consent');
    authUrl.searchParams.set('state', state);

    window.location.href = authUrl.toString();
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // 인앱 브라우저: 경고 + 외부 브라우저 안내
  if (inAppInfo.isInApp) {
    return (
      <div style={{
        padding: '16px',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: 'var(--radius-lg, 12px)',
        textAlign: 'center',
      }}>
        <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#664d03', fontWeight: 600 }}>
          {inAppInfo.name} 앱 내 브라우저에서는<br/>Google 로그인이 제한됩니다.
        </p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => openInExternalBrowser()}
            type="button"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '12px 20px',
              backgroundColor: '#0d6efd',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            &#x1F310; 외부 브라우저로 열기
          </button>
          <button
            onClick={handleCopyUrl}
            type="button"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '12px 20px',
              backgroundColor: copied ? '#198754' : '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'background-color 0.2s',
            }}
          >
            {copied ? '\u2705 복사됨!' : '\u{1F4CB} URL 복사'}
          </button>
        </div>
        <p style={{ margin: '10px 0 0 0', fontSize: '0.8rem', color: '#664d03' }}>
          복사한 주소를 Chrome/Safari에 붙여넣어 주세요
        </p>
      </div>
    );
  }

  return (
    <button
      onClick={handleGoogleLogin}
      type="button"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        width: '100%',
        padding: '14px 24px',
        border: '2px solid #4285f4',
        borderRadius: 'var(--radius-lg, 12px)',
        background: 'white',
        fontSize: '16px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        color: '#4285f4',
        fontFamily: 'inherit',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.background = '#4285f4';
        e.currentTarget.style.color = 'white';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(66, 133, 244, 0.3)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.background = 'white';
        e.currentTarget.style.color = '#4285f4';
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      <svg viewBox="0 0 24 24" width="20" height="20" style={{ flexShrink: 0 }}>
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
      </svg>
      <span>Google로 로그인</span>
    </button>
  );
}

function generateRandomState() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}
